import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Button, Tooltip, ActionIcon, Loader, SegmentedControl, Group, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconDownload, IconTable, IconLayoutGrid } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { DynamicTableView } from '../common/DynamicTableView';
import { type TableColumn, type TableAction, type ListResponse } from '../common/GenericTable';
import { getPluralizedLabel, getSingularLabel } from '../../utils/pluralizationUtils';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityCategorizationService } from '../../services/EntityCategorizationService';
import { WorkflowStepEditDialog } from '../dialogs';
import { WorkflowDesigner } from '../workflows/WorkflowDesigner';
import { GenericEntityUpsertDialog } from '../dialogs';
import { isEntityType, EntityType } from '../../utils/entityTypeDetection';
import { getEntityConfig } from '../../registry';
import { needsAttributeFetch, wasAttributeRequested } from '../../utils/entityPropertyUtils';
import { useEntityEntitlements } from '../../hooks/useEntityEntitlements';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { getReadableAttributeIdsForEntity, getNonIndexedReadableAttributeNamesFromTargetResponse, getReadableAttributeIdsFromTargetResponse } from '../../types/EntityEntitlements';
import { backOrReplaceHashRoute, isCurrentHashPath, pushHashRoute, replaceHashRoute } from '../../utils/hashNavigationUtils';
import { UnifiedEntityScreen } from './UnifiedEntityScreen';
import { debugLog } from '../../utils/debugLog';
import { buildEntityScreenHash } from '../../utils/entityScreenRouteUtils';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import { ShadcnCardGrid } from './ShadcnCardGrid';
import { getEntityCardFieldsForEntity } from '../../registry/entityCardFields';

interface DynamicEntityManagementProps {
    entityType: string;
    selectedEntityId?: string | null;
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    selectedEntityType?: string;
    /** When this changes (e.g. on-behalf-of toggled or UPN set/cleared), the table refreshes so data reflects the effective user. */
    onBehalfOfKey?: string;
    /** When set (e.g. 'admin/pluginfunctions/table'), hash uses this base for list and entity deep links instead of category-based #admin/entityType. */
    listViewHashBase?: string;
    // Allow caller to provide custom columns or column generation
    generateColumns?: () => TableColumn<any>[];
    // Allow caller to provide additional columns to merge with default
    additionalColumns?: TableColumn<any>[];
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}

/** Omit keys that start with "_" (e.g. _attributesRequested) so clone does not copy internal metadata. */
function omitInternalKeys(obj: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
        if (k.startsWith('_')) continue;
        out[k] = obj[k];
    }
    return out;
}

/**
 * Dynamic Entity Management component using DynamicTableView
 *
 * This component handles CRUD operations for any entity type with:
 * - Dynamic table columns based on entity type
 * - Entity detail dialog
 * - Edit/Clone/Delete functionality
 * - URL routing based on entity category
 */
const DynamicEntityManagement: React.FC<DynamicEntityManagementProps> = ({
    entityType,
    selectedEntityId,
    selectedEntityViewMode,
    deepLinkEntityType,
    authResolved = false,
    selectedEntityType,
    onBehalfOfKey,
    listViewHashBase,
    generateColumns: callerGenerateColumns,
    additionalColumns,
    onUnsavedStateChange
}) => {
    const { isStandard } = useViewModeSafe();
    const queryClient = useQueryClient();
    const [selectedEntity, setSelectedEntity] = useState<any>(null);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingEntity, setEditingEntity] = useState<any>(null);
    const [editingTargetEntitlement, setEditingTargetEntitlement] = useState<any>(undefined);
    const [loadingEntityId, setLoadingEntityId] = useState<string | null>(null);
    const [cloneSetOpen, setCloneSetOpen] = useState(false);
    const [cloneSetEntity, setCloneSetEntity] = useState<any>(null);
    const [clonePolicyOpen, setClonePolicyOpen] = useState(false);
    const [clonePolicyEntity, setClonePolicyEntity] = useState<any>(null);
    const [cloneEmailOpen, setCloneEmailOpen] = useState(false);
    const [cloneEmailEntity, setCloneEmailEntity] = useState<any>(null);
    const [cloneRequestApprovalTypeOpen, setCloneRequestApprovalTypeOpen] = useState(false);
    const [cloneRequestApprovalTypeEntity, setCloneRequestApprovalTypeEntity] = useState<any>(null);
    const [cloneGenericOpen, setCloneGenericOpen] = useState(false);
    const [cloneGenericEntity, setCloneGenericEntity] = useState<any>(null);
    const [cloneGenericEntityType, setCloneGenericEntityType] = useState<string>('');
    const [deletingEntityId, setDeletingEntityId] = useState<string | null>(null);
    const [deletionErrors, setDeletionErrors] = useState<Map<string, string>>(new Map());
    const [downloadingContentId, setDownloadingContentId] = useState<string | null>(null);
    const [listResponse, setListResponse] = useState<ListResponse<any> | null>(null);
    const [inlineCreateOpen, setInlineCreateOpen] = useState(false);
    const newUiOpen = !!selectedEntityId
        && (selectedEntityViewMode === 'view' || selectedEntityViewMode === 'edit');
    /** When true, we just opened edit from the table pencil; skip opening the detail dialog and do not set hash (avoids duplicate entity + tier3 fetches). */
    const openingEditFromTableRef = useRef(false);
    /** When we opened the edit dialog (timestamp); avoid closing immediately before hash has updated. */
    const editDialogOpenedAtRef = useRef<number>(0);
    /** Set when closing edit dialog so the "open edit from URL" effect skips one run and doesn't re-open. */
    const editDialogClosingRef = useRef(false);

    const storageKey = `hades_entity_list_view_mode_${entityType.toLowerCase()}`;
    const [listViewMode, setListViewMode] = useState<'table' | 'cards'>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored === 'cards' || stored === 'table') return stored;
        } catch { /* noop */ }
        return 'table';
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, listViewMode); } catch { /* noop */ }
    }, [storageKey, listViewMode]);

    const { entityEntitlements } = useEntityEntitlements();
    const allowedSearchAttributeIds = React.useMemo(
        () => getReadableAttributeIdsForEntity(entityEntitlements ?? null, entityType),
        [entityEntitlements, entityType]
    );

    // Tier 2 (per-entity schema) entitlements â€“ view/edit entity fetch must wait until this is resolved
    const { allowedTableAttributeIds } = usePerEntityTableEntitlement({
        entityType: entityType?.trim(),
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet: false
    });
    const isTier2Resolved = allowedTableAttributeIds !== null;

    // Registry-driven editor type: determines which edit dialog to render
    const editorType = getEntityConfig(entityType)?.editor ?? 'generic';
    // Check if this is the "All Users" (Person) view - used to show Type column for subtypes
    const isPersonView = isEntityType(entityType, EntityType.Person);

    const handleContentDownload = useCallback(async (entity: any) => {
        const entityId = entity.Id || entity.id;
        if (!entityId) return;
        setDownloadingContentId(entityId);
        try {
            const full = await entityService.getEntityById(entityId, 'Content', true, ['Id', 'DisplayName', 'Body', 'ContentType']);
            const body = full?.Body ?? full?.body ?? '';
            const contentType = full?.ContentType ?? full?.contentType ?? entity.ContentType ?? 'text/plain';
            const displayName = full?.DisplayName ?? full?.displayName ?? entity.DisplayName ?? entityId;

            const extMap: Record<string, string> = {
                'text/html': '.html',
                'text/markdown': '.md',
                'text/plain': '.txt',
                'text/css': '.css',
                'text/csv': '.csv',
                'text/xml': '.xml',
                'application/json': '.json',
                'application/xml': '.xml',
            };
            const ext = extMap[(contentType || '').toLowerCase().split(';')[0].trim()] ?? '.txt';
            const safeName = displayName.replace(/[<>:"/\\|?*]+/g, '_');
            const filename = safeName.endsWith(ext) ? safeName : `${safeName}${ext}`;

            const blob = new Blob([body], { type: contentType || 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.error('Failed to download content:', err);
        } finally {
            setDownloadingContentId(null);
        }
    }, [entityService]);

    // When onBehalfOfKey changes (e.g. x-on-behalf-of toggled or UPN changed), refresh table so data reflects effective user
    const prevOnBehalfOfKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const current = onBehalfOfKey ?? '';
        const prev = prevOnBehalfOfKeyRef.current;
        prevOnBehalfOfKeyRef.current = current;
        if (prev !== undefined && prev !== current) setRefreshSignal((v) => v + 1);
    }, [onBehalfOfKey]);

    // When user navigates back (e.g. browser back or app back), selectedEntityId is cleared or changes.
    // Close the edit dialog so it does not stay open over the refreshed list.
    useEffect(() => {
        if (!editDialogOpen || !editingEntity) return;
        // Avoid closing right after we opened (give hash time to update selectedEntityId)
        if (Date.now() - editDialogOpenedAtRef.current < 300) return;
        const editingId = editingEntity?.Id ?? editingEntity?.id;
        if (selectedEntityId !== editingId) {
            setEditDialogOpen(false);
            setEditingEntity(null);
            setEditingTargetEntitlement(undefined);
            setSelectedEntity(null);
        }
    }, [selectedEntityId, editDialogOpen, editingEntity]);

    // When user navigates back from clone create (e.g. browser back), URL leaves create â†’ close clone dialogs so we don't stay on create dialog.
    useEffect(() => {
        if (selectedEntityViewMode === 'create') return;
        if (cloneSetOpen || clonePolicyOpen || cloneEmailOpen || cloneRequestApprovalTypeOpen || cloneGenericOpen) {
            setCloneSetOpen(false);
            setCloneSetEntity(null);
            setClonePolicyOpen(false);
            setClonePolicyEntity(null);
            setCloneEmailOpen(false);
            setCloneEmailEntity(null);
            setCloneRequestApprovalTypeOpen(false);
            setCloneRequestApprovalTypeEntity(null);
            setCloneGenericOpen(false);
            setCloneGenericEntity(null);
            setCloneGenericEntityType('');
        }
    }, [selectedEntityViewMode]);

    useEffect(() => {
        if (!newUiOpen) return;
        setEditDialogOpen(false);
    }, [newUiOpen]);

    // Reset inline create when navigating to a different entity type
    useEffect(() => {
        setInlineCreateOpen(false);
    }, [entityType]);

    // Build list-view hash (for create dialog close and clone dialog close so URL reflects table view)
    const getListHash = useCallback(async (): Promise<string> => {
        const normalizedEntityType = entityType.toLowerCase();
        if (listViewHashBase) return `#${listViewHashBase}`;
        if (normalizedEntityType === 'group') return '#groups';
        if (normalizedEntityType === 'class') return '#classes';
        const categorizationService = EntityCategorizationService.getInstance();
        const category = await categorizationService.getEntityCategory(normalizedEntityType);
        if (category.type === 'person') return `#Person/${normalizedEntityType}`;
        if (category.type === 'locality') return `#localities/${normalizedEntityType}`;
        if (category.type === 'admin') return `#admin/${normalizedEntityType}`;
        return `#other/${normalizedEntityType}`;
    }, [entityType, listViewHashBase]);

    const getUnifiedHash = useCallback(async (mode: 'view' | 'edit', entityId: string): Promise<string> => {
        const listHash = await getListHash();
        const currentHash = (typeof window !== 'undefined' ? window.location.hash : '') || '';
        const idx = currentHash.indexOf('?');
        const currentSearch = idx >= 0 ? currentHash.slice(idx) : '';
        const next = buildEntityScreenHash(listHash, mode, entityId);
        return currentSearch && !next.includes('?') ? `${next}${currentSearch}` : next;
    }, [getListHash]);

    // Handle entity selection
    const handleEntitySelect = useCallback(async (entity: any) => {
        // If we just opened edit from the table pencil, do not open the detail dialog (avoids duplicate fetches and showing view instead of edit)
        if (openingEditFromTableRef.current) {
            openingEditFromTableRef.current = false;
            return;
        }
        // If edit dialog is open, the hash may have been set by "Edit" (view or table); do not open the detail view
        if (editDialogOpen) {
            return;
        }
        // Reload / deep link: when URL is edit, do not open view or overwrite hash with view (table still calls this from its deep-link effect)
        if (typeof window !== 'undefined' && /\/edit\//i.test((window.location.hash || ''))) {
            return;
        }

        debugLog(`ðŸ” DynamicEntityManagement: Entity selected:`, entity);

        const currentHash = (typeof window !== 'undefined' ? window.location.hash : '') || '';
        const currentSearch = (() => {
            const idx = currentHash.indexOf('?');
            return idx >= 0 ? currentHash.slice(idx) : '';
        })();
        const withPreservedHashSearch = (hashRoute: string): string => {
            if (!currentSearch) return hashRoute;
            return hashRoute.includes('?') ? hashRoute : `${hashRoute}${currentSearch}`;
        };

        // Extract entity ID
        const entityId = typeof entity === 'string' ? entity : (entity?.Id || entity?.id || '');

        // Do not start view entity fetch until tier 2 entitlements are resolved (avoids race with cached tier2)
        if (!entityId || !isTier2Resolved) {
            if (entityId && !isTier2Resolved) {
                void getUnifiedHash('view', entityId).then((hash) => {
                    const next = withPreservedHashSearch(hash);
                    if (!isCurrentHashPath(next)) {
                        pushHashRoute(next);
                    }
                });
            }
            return;
        }

        // Do not prefetch full entity here (unbounded attributes).
        // Pass row entity/stub and let useEntityDetailLoader fetch detail with tier3-indexed attributes.
        const fullEntity = typeof entity === 'string' ? { Id: entityId } : entity;

        setSelectedEntity(fullEntity);
        if (entityId) {
            const newHash = withPreservedHashSearch(await getUnifiedHash('view', entityId));
            if (!isCurrentHashPath(newHash)) {
                pushHashRoute(newHash);
            }
        }
    }, [editDialogOpen, entityType, getUnifiedHash, isTier2Resolved]);

    // Create a refresh function that can be called after deletion or edit
    const handleRefresh = useCallback(() => {
        setRefreshSignal((v) => v + 1);
        // Clear deletion errors on refresh
        setDeletionErrors(new Map());
    }, []);

    // Handle edit entity
    const handleEditEntity = useCallback((entity: any) => {
        debugLog(`âœï¸ DynamicEntityManagement: Editing entity:`, entity);
        editDialogOpenedAtRef.current = Date.now();
        setEditingEntity(entity);
        setEditDialogOpen(true);
    }, []);

    // Handle edit from table row pencil (skip view, go straight to edit).
    // When listViewHashBase is set (e.g. table tab), update hash to deep link so URL reflects edit state and close returns to table.
    // WorkflowActivity: always eager-load full entity for edit (table doesn't request Settings; editing is mostly for that).
    const handleEditFromTable = useCallback(async (entity: any) => {
        openingEditFromTableRef.current = true;
        const entityId = entity?.Id || entity?.id;
        editDialogOpenedAtRef.current = Date.now();
        let entityToEdit = entity;
        // WorkflowActivity: fetch Settings/Script only when not already in _attributesRequested (e.g. force-loaded in view)
        if (entityId && entityType && isEntityType(entityType, EntityType.WorkflowActivity)) {
            const needSettings = !wasAttributeRequested(entity, 'Settings');
            const needScript = !wasAttributeRequested(entity, 'Script');
            if (needSettings || needScript) {
                try {
                    const attrs = ['Id', 'DisplayName', 'Description', 'Settings', 'Script', 'Action', 'Authorization', 'EntityType'];
                    const full = await entityService.getEntityById(entityId, entityType, false, attrs);
                    if (full && typeof full === 'object') entityToEdit = full;
                } catch {
                    // use row entity
                }
            }
        }
        // Workflow: same when opening from table so edit has Settings but we don't request Config
        if (entityId && entityType && isEntityType(entityType, EntityType.Workflow)) {
            try {
                const attrs = ['Id', 'DisplayName', 'Type', 'Settings'];
                const full = await entityService.getEntityById(entityId, entityType, false, attrs);
                if (full && typeof full === 'object') entityToEdit = full;
            } catch {
                // use row entity
            }
        }
        // File: fetch entity with ContentType when _attributesRequested didn't include it (avoids duplicate fetch when list already requested it)
        if (entityId && entityType && isEntityType(entityType, EntityType.File) && needsAttributeFetch(entity, 'ContentType')) {
            try {
                const full = await entityService.getEntityById(entityId, entityType, false, ['Id', 'DisplayName', 'ContentType']);
                if (full && typeof full === 'object') entityToEdit = { ...entityToEdit, ...full };
            } catch {
                // use row entity
            }
        }
        setEditingEntity(entityToEdit);
        setEditingTargetEntitlement(undefined);
        if (entityId) {
            const newHash = await getUnifiedHash('edit', entityId);
            pushHashRoute(newHash);
        }
    }, [entityService, entityType, getUnifiedHash]);

    // Handle edit dialog close - navigate to list view so detail dialog does not re-open
    const handleEditDialogClose = useCallback(async (hasChanges?: boolean) => {
        editDialogClosingRef.current = true;
        setEditDialogOpen(false);
        setEditingEntity(null);
        setEditingTargetEntitlement(undefined);
        setSelectedEntity(null);

        const normalizedEntityType = entityType.toLowerCase();

        // Update URL hash to list view (no entity ID) so App clears selectedEntityId and detail does not re-open
        let newHash: string;
        if (listViewHashBase) {
            newHash = `#${listViewHashBase}`;
        } else if (normalizedEntityType === 'group') {
            newHash = '#groups';
        } else if (normalizedEntityType === 'class') {
            newHash = '#classes';
        } else {
            const categorizationService = EntityCategorizationService.getInstance();
            const category = await categorizationService.getEntityCategory(normalizedEntityType);
            if (category.type === 'person') {
                newHash = `#Person/${normalizedEntityType}`;
            } else if (category.type === 'locality') {
                newHash = `#localities/${normalizedEntityType}`;
            } else if (category.type === 'admin') {
                newHash = `#admin/${normalizedEntityType}`;
            } else {
                newHash = `#other/${normalizedEntityType}`;
            }
        }

        debugLog(`ðŸ”— DynamicEntityManagement: Updating URL hash after edit close to list: ${newHash}`);
        // Preserve current list search params when closing edit (they're appended to edit hashes).
        const currentHash = (typeof window !== 'undefined' ? window.location.hash : '') || '';
        const idx = currentHash.indexOf('?');
        const currentSearch = idx >= 0 ? currentHash.slice(idx) : '';
        const hashWithSearch = currentSearch && !newHash.includes('?') ? `${newHash}${currentSearch}` : newHash;
        replaceHashRoute(hashWithSearch);

        if (hasChanges) {
            handleRefresh();
        }
    }, [handleRefresh, entityType, listViewHashBase]);

    // Handle edit success
    const handleEditSuccess = useCallback(() => {
        handleEditDialogClose(true); // Always refresh on successful save
    }, [handleEditDialogClose]);

    const handleCloseCreateDialog = useCallback(async () => {
        const newHash = await getListHash();
        pushHashRoute(newHash);
    }, [getListHash]);

    const handleInlineCreate = useCallback(() => {
        setInlineCreateOpen(true);
    }, []);

    const handleInlineCreateClose = useCallback(() => {
        setInlineCreateOpen(false);
    }, []);

    const handleInlineCreateSuccess = useCallback(() => {
        setInlineCreateOpen(false);
        handleRefresh();
    }, [handleRefresh]);

    const closeCloneDialogAndSyncHash = useCallback((close: () => void) => {
        getListHash().then((newHash) => {
            pushHashRoute(newHash);
            close();
        });
    }, [getListHash]);

    // Map column key/title to API attribute id(s) for tier2 entitlement filtering
    const getColumnAttributeIds = useCallback((col: TableColumn<any>): string[] => {
        if (col.title) return [col.title];
        const keyToAttr: Record<string, string> = {
            id: 'Id', displayname: 'DisplayName', type: 'EntityType', description: 'Description',
            role: 'Role', department: 'Department', subject: 'Subject', tags: 'Tags',
            filterscope: 'FilterScope', issystem: 'IsSystem', disabled: 'Disabled',
            userprincipalname: 'UserPrincipalName', contenttype: 'ContentType', filename: 'FileName',
            modificationtype: 'ModificationType', target: 'Target', requestor: 'Requestor',
            requestcreated: 'RequestCreated', requestexpiration: 'RequestExpiration',
            direction: 'Direction', targetset: 'TargetSet', actionworkflow: 'ActionWorkflow',
            isentityset: 'IsEntitySet', disjointed: 'Disjointed', invokable: 'Invokable',
            isreference: 'IsReference', computed: 'Computed', required: 'Required',
            array: 'Array', indexed: 'Indexed', unique: 'Unique'
        };
        const single = keyToAttr[col.key];
        if (single) return [single];
        // WorkflowActivity Type column is computed from Action, Authorization, Request
        if (col.key === 'type' && isEntityType(entityType, EntityType.WorkflowActivity)) {
            return ['Action', 'Authorization', 'Request'];
        }
        // Workflow Type column
        if (col.key === 'type') return ['Type'];
        return [col.key];
    }, [entityType]);

    // Generate default table columns via registry; special-case Content download & Person type column
    const defaultGenerateColumns = useCallback((): TableColumn<any>[] => {
        const config = getEntityConfig(entityType);
        const columns: TableColumn<any>[] = [
            {
                key: 'displayname',
                label: 'Display Name',
                title: 'DisplayName',
                sortable: true,
                render: (entity: any) => entity.DisplayName || entity.Name || entity.displayName || entity.name || 'N/A'
            }
        ];

        // Person "All Users" view shows EntityType as Type column
        if (isPersonView) {
            columns.push({
                key: 'type',
                label: 'Type',
                sortable: true,
                render: (entity: any) => entity.EntityType || entity.Type || entity.type || 'N/A'
            });
        }

        // Registry-driven columns
        if (config?.listColumns) {
            columns.push(...config.listColumns({ isStandard }));
        }

        // Content download action column (needs DEM closure state)
        if (isEntityType(entityType, EntityType.Content)) {
            columns.push({
                key: '_download',
                label: '',
                sortable: false,
                render: (entity: any) => {
                    const id = entity.Id || entity.id;
                    const isDownloading = downloadingContentId === id;
                    return (
                        <Tooltip label="Download content">
                            <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    if (!isDownloading) handleContentDownload(entity);
                                }}
                                disabled={isDownloading}
                                aria-label="Download content"
                            >
                                {isDownloading ? <Loader size={14} /> : <IconDownload size={16} />}
                            </ActionIcon>
                        </Tooltip>
                    );
                }
            });
        }

        // Additional columns from caller
        if (additionalColumns && additionalColumns.length > 0) {
            columns.push(...additionalColumns);
        }

        // Id column (hidden for types that declare hideIdInStandard in the registry)
        const hideId = isStandard && !!config?.hideIdInStandard;
        if (!hideId) {
            columns.push({
                key: 'id',
                label: 'Id',
                title: 'Id',
                sortable: true,
                render: (entity: any) => entity.Id || entity.id || 'N/A'
            });
        }

        // Filter columns by tier2 entitlements when restricted (non-admin)
        if (Array.isArray(allowedTableAttributeIds)) {
            const allowedLower = new Set(allowedTableAttributeIds.map((a) => a.toLowerCase()));
            const alwaysShow = new Set(['id', 'displayname']);
            return columns.filter((c) => {
                const attrIds = getColumnAttributeIds(c);
                const allowedAny = attrIds.some((id) => allowedLower.has(id.toLowerCase()));
                const alwaysShowKey = alwaysShow.has(c.key.toLowerCase());
                return alwaysShowKey || allowedAny;
            });
        }
        return columns;
    }, [entityType, additionalColumns, isPersonView, allowedTableAttributeIds, getColumnAttributeIds, isStandard]);

    // Use caller's generateColumns if provided, otherwise use default
    // For Journal Entry, let DynamicTableView handle column generation (it manages the TargetType dropdown)
    const isJournalEntryType = isEntityType(entityType, EntityType.JournalEntry);
    const generateColumns = useCallback((): TableColumn<any>[] => {
        if (callerGenerateColumns) {
            return callerGenerateColumns();
        }
        return defaultGenerateColumns();
    }, [callerGenerateColumns, defaultGenerateColumns]);

    // Actions are now handled in detail view, not in table
    const generateActions = useCallback((): TableAction<any>[] => {
        return [];
    }, []);

    const entityCardRenderer = useCallback((props: {
        data: any[];
        isLoading: boolean;
        currentPage: number;
        totalResults: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
        onRowClick?: (item: any) => void;
    }) => {
        return (
            <ShadcnCardGrid
                data={props.data}
                entityType={entityType}
                isLoading={props.isLoading}
                currentPage={props.currentPage}
                totalResults={props.totalResults}
                itemsPerPage={props.itemsPerPage}
                onPageChange={props.onPageChange}
                onEntitySelect={(entity) => props.onRowClick?.(entity)}
                getCardFields={(entity) => getEntityCardFieldsForEntity(entityType, entity)}
            />
        );
    }, [entityType]);

    const viewToggle = useMemo(() => (
        <SegmentedControl
            value={listViewMode}
            onChange={(val) => setListViewMode(val as 'table' | 'cards')}
            size="xs"
            data={[
                { label: <Group gap={4} wrap="nowrap"><IconTable size={14} /><Text size="xs">Table</Text></Group>, value: 'table' },
                { label: <Group gap={4} wrap="nowrap"><IconLayoutGrid size={14} /><Text size="xs">Cards</Text></Group>, value: 'cards' },
            ]}
        />
    ), [listViewMode]);

    // Handle clearing row errors
    const handleClearRowError = useCallback((rowKey: string) => {
        setDeletionErrors(prev => {
            const newMap = new Map(prev);
            newMap.delete(rowKey);
            return newMap;
        });
    }, []);

    const showTable = !newUiOpen && !inlineCreateOpen;

    return (
        <>
            {showTable && (
                <DynamicTableView
                    entityType={entityType}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    selectedEntityType={selectedEntityType}
                    generateColumns={isJournalEntryType && !callerGenerateColumns ? undefined : generateColumns}
                    customHeaderContent={viewToggle}
                    customDataRenderer={listViewMode === 'cards' ? entityCardRenderer : undefined}
                    onEntitySelect={handleEntitySelect}
                    onEditRowClick={handleEditFromTable}
                    onListResponse={setListResponse}
                    refreshSignal={refreshSignal}
                    onBehalfOfKey={onBehalfOfKey}
                    allowedSearchAttributeIds={allowedSearchAttributeIds}
                    rowErrors={deletionErrors}
                    onClearRowError={handleClearRowError}
                    onCloseCreateDialog={handleCloseCreateDialog}
                    onInlineCreate={handleInlineCreate}
                />
            )}

            {/* Inline Create View */}
            {inlineCreateOpen && (
                <Box mt="md" pb="lg">
                    <Group justify="space-between" align="center" mb="md" role="toolbar" aria-label="Create navigation">
                        <Group gap="sm" align="center">
                            <Button
                                variant="default"
                                leftSection={<IconArrowLeft size={16} />}
                                onClick={handleInlineCreateClose}
                            >
                                Back to List
                            </Button>
                            <Title order={4} m={0}>
                                Create {getSingularLabel(entityType)}
                            </Title>
                        </Group>
                    </Group>
                    {editorType === 'workflowStep' ? (
                        <WorkflowStepEditDialog
                            open={true}
                            onClose={handleInlineCreateClose}
                            onSuccess={handleInlineCreateSuccess}
                            entityType="WorkflowActivity"
                            presentation="inline"
                        />
                    ) : editorType === 'workflowDesigner' ? (
                        <WorkflowDesigner
                            open={true}
                            onClose={handleInlineCreateClose}
                            onSuccess={handleInlineCreateSuccess}
                            entityType="workflow"
                            presentation="inline"
                        />
                    ) : (
                        <GenericEntityUpsertDialog
                            key={`inline-create-${entityType}`}
                            open={true}
                            onClose={handleInlineCreateClose}
                            onSuccess={handleInlineCreateSuccess}
                            entityType={entityType}
                            presentation="inline"
                        />
                    )}
                </Box>
            )}


            {/* Edit Dialogs -- legacy modal fallback (used only when hash-routing does not set newUiOpen) */}
            {editingEntity && editorType === 'workflowStep' && (
                <WorkflowStepEditDialog
                    open={editDialogOpen && !newUiOpen}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                    step={editingEntity}
                    entityType="WorkflowActivity"
                />
            )}

            {editingEntity && editorType === 'workflowDesigner' && (
                <WorkflowDesigner
                    open={editDialogOpen && !newUiOpen}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                    workflow={editingEntity}
                    entityType="workflow"
                />
            )}

            {editingEntity && editorType === 'generic' && (
                <GenericEntityUpsertDialog
                    key={`edit-${entityType}-${editingEntity.Id || editingEntity.id}`}
                    open={editDialogOpen && !newUiOpen}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                    entityType={entityType}
                    entityId={editingEntity.Id || editingEntity.id}
                    existingEntity={editingEntity}
                    targetEntitlement={editingTargetEntitlement}
                    onOpenNewUi={async () => {
                        const entityId = editingEntity?.Id || editingEntity?.id;
                        if (!entityId) return;
                        const newHash = await getUnifiedHash('edit', entityId);
                        pushHashRoute(newHash);
                        setEditDialogOpen(false);
                    }}
                />
            )}

            <UnifiedEntityScreen
                key={`${entityType}-${selectedEntityId ?? ''}`}
                open={newUiOpen}
                entityType={entityType}
                entityId={selectedEntityId ?? ''}
                mode={selectedEntityViewMode === 'edit' ? 'edit' : 'view'}
                existingEntity={editingEntity ?? selectedEntity}
                targetEntitlement={editingTargetEntitlement}
                onClose={() => { void handleEditDialogClose(false); }}
                onModeChange={(mode) => {
                    if (!selectedEntityId) return;
                    void getUnifiedHash(mode, selectedEntityId).then((hash) => replaceHashRoute(hash));
                }}
                onSuccess={handleEditSuccess}
                onUnsavedStateChange={onUnsavedStateChange}
            />

            {/* Create Dialog for Content - removed, DynamicTableView handles it automatically */}

            {/* Clone Set Dialog */}
            {cloneSetOpen && isEntityType(entityType, EntityType.Set) && (
                <GenericEntityUpsertDialog
                    key={`clone-set-${cloneSetEntity?.Id || cloneSetEntity?.id || 'new'}`}
                    open={cloneSetOpen}
                    onClose={() => closeCloneDialogAndSyncHash(() => { setCloneSetOpen(false); setCloneSetEntity(null); })}
                    onSuccess={() => { setCloneSetOpen(false); setCloneSetEntity(null); handleRefresh(); }}
                    entityType="Set"
                    existingEntity={cloneSetEntity ? { ...omitInternalKeys(cloneSetEntity) as any, Id: undefined, DisplayName: cloneSetEntity.DisplayName ? `${cloneSetEntity.DisplayName} (Copy)` : undefined } : undefined}
                />
            )}

            {/* Clone Policy Rule Dialog */}
            {clonePolicyOpen && isEntityType(entityType, EntityType.PolicyRule) && (
                <GenericEntityUpsertDialog
                    key={`clone-policyrule-${clonePolicyEntity?.Id || clonePolicyEntity?.id || 'new'}`}
                    open={clonePolicyOpen}
                    onClose={() => closeCloneDialogAndSyncHash(() => { setClonePolicyOpen(false); setClonePolicyEntity(null); })}
                    onSuccess={() => { setClonePolicyOpen(false); setClonePolicyEntity(null); handleRefresh(); }}
                    entityType="PolicyRule"
                    existingEntity={clonePolicyEntity ? { ...omitInternalKeys(clonePolicyEntity) as any, Id: undefined } : undefined}
                />
            )}

            {/* Clone Email Template Dialog */}
            {cloneEmailOpen && isEntityType(entityType, EntityType.EmailTemplate) && (
                <GenericEntityUpsertDialog
                    key={`clone-emailtemplate-${cloneEmailEntity?.Id || cloneEmailEntity?.id || 'new'}`}
                    open={cloneEmailOpen}
                    onClose={() => closeCloneDialogAndSyncHash(() => { setCloneEmailOpen(false); setCloneEmailEntity(null); })}
                    onSuccess={() => { setCloneEmailOpen(false); setCloneEmailEntity(null); handleRefresh(); }}
                    entityType="EmailTemplate"
                    existingEntity={cloneEmailEntity ? { ...omitInternalKeys(cloneEmailEntity) as any, Id: undefined, DisplayName: cloneEmailEntity.DisplayName ? `${cloneEmailEntity.DisplayName} (Copy)` : undefined } : undefined}
                />
            )}

            {/* Clone Request Approval Type Dialog */}
            {cloneRequestApprovalTypeOpen && isEntityType(entityType, EntityType.RequestApprovalType) && (
                <GenericEntityUpsertDialog
                    key={`clone-requestapprovaltype-${cloneRequestApprovalTypeEntity?.Id || cloneRequestApprovalTypeEntity?.id || 'new'}`}
                    open={cloneRequestApprovalTypeOpen}
                    onClose={() => closeCloneDialogAndSyncHash(() => { setCloneRequestApprovalTypeOpen(false); setCloneRequestApprovalTypeEntity(null); })}
                    onSuccess={() => { setCloneRequestApprovalTypeOpen(false); setCloneRequestApprovalTypeEntity(null); handleRefresh(); }}
                    entityType="RequestApprovalType"
                    existingEntity={cloneRequestApprovalTypeEntity ? { ...omitInternalKeys(cloneRequestApprovalTypeEntity) as any, Id: undefined, DisplayName: cloneRequestApprovalTypeEntity.DisplayName ? `${cloneRequestApprovalTypeEntity.DisplayName} (Copy)` : undefined } : undefined}
                />
            )}

            {/* Generic clone dialog for all other entity types */}
            {cloneGenericOpen && cloneGenericEntity && cloneGenericEntityType && (
                <GenericEntityUpsertDialog
                    key={`clone-generic-${cloneGenericEntityType}-${cloneGenericEntity?.Id || cloneGenericEntity?.id || 'new'}`}
                    open={cloneGenericOpen}
                    onClose={() => closeCloneDialogAndSyncHash(() => { setCloneGenericOpen(false); setCloneGenericEntity(null); setCloneGenericEntityType(''); })}
                    onSuccess={() => { setCloneGenericOpen(false); setCloneGenericEntity(null); setCloneGenericEntityType(''); handleRefresh(); }}
                    entityType={cloneGenericEntityType}
                    existingEntity={{ ...omitInternalKeys(cloneGenericEntity) as any, Id: undefined, id: undefined, DisplayName: cloneGenericEntity.DisplayName ? `${cloneGenericEntity.DisplayName} (Copy)` : cloneGenericEntity.DisplayName }}
                />
            )}
        </>
    );
};

export default DynamicEntityManagement;

